import { useState, useEffect } from 'react';
import { devLog } from '@/utils/logger';
import { generatePreviewPayslipPdf, downloadBlob } from '@/services/payslips/previewPdfGenerator';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { 
  CalendarIcon, 
  Download, 
  Calculator, 
  AlertCircle, 
  CheckCircle,
  Users,
  FileText,
  Activity,
  Settings
} from 'lucide-react';
import { format, addWeeks, startOfWeek, endOfWeek, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { cn } from '@/lib/utils';
import { usePayrollPreview } from '@/hooks/compensation';
import { useCompanyEmployees } from '@/hooks/leave/useCompanyEmployees';
import { useWorkplaceCurrency } from '@/contexts/WorkplaceCurrencyContext';
import { PayslipPreviewCard } from './PayslipPreviewCard';
import { RealTimeCalculationEngine } from './RealTimeCalculationEngine';
import { PayrollExportDialog } from './PayrollExportDialog';
import type { PayrollPreviewResult } from '@/services/compensation/types';

interface CompensationPreviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  selectedEmployeeIds?: string[];
}

export function CompensationPreviewDialog({
  open,
  onOpenChange,
  selectedEmployeeIds = [],
}: CompensationPreviewDialogProps) {
  const [startDate, setStartDate] = useState<Date>(startOfMonth(subMonths(new Date(), 1)));
  const [endDate, setEndDate] = useState<Date>(endOfMonth(subMonths(new Date(), 1)));
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState<PayrollPreviewResult | null>(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [startDateOpen, setStartDateOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);
  
  const { currencyCode } = useWorkplaceCurrency();
  const { data: employees, isLoading: isLoadingEmployees } = useCompanyEmployees();
  
  // Calculate employee IDs to use for the query
  const employeeIds = selectedEmployeeIds.length > 0 
    ? selectedEmployeeIds 
    : employees?.map(e => e.id) || [];
  
  const { 
    data: payrollPreview, 
    isLoading: isLoadingPreview,
    isFetching,
    error,
    refetch
  } = usePayrollPreview(
    employeeIds,
    startDate,
    endDate,
    open && employeeIds.length > 0
  );
  
  // Combined loading state
  const isLoading = (open && isLoadingEmployees) || isLoadingPreview || isFetching;

  // Log when query parameters change (for debugging)
  useEffect(() => {
    if (open) {
      devLog('[CompensationPreview] Dialog opened with params', {
        startDate: startDate.toISOString().split('T')[0],
        endDate: endDate.toISOString().split('T')[0],
        selectedEmployeeCount: selectedEmployeeIds.length,
        totalEmployeeCount: employees?.length || 0,
        employeeIdsToUse: employeeIds.length,
        queryEnabled: open && employeeIds.length > 0,
        isLoadingEmployees,
        isLoadingPreview,
        isFetching,
      });
    }
  }, [open, startDate, endDate, selectedEmployeeIds.length, employees?.length, employeeIds.length, isLoadingEmployees, isLoadingPreview, isFetching]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode,
    }).format(amount);
  };

  const getTotalSummary = () => {
    if (!payrollPreview?.length) return { totalGross: 0, totalNet: 0, totalHours: 0 };
    
    return payrollPreview.reduce(
      (acc, result) => ({
        totalGross: acc.totalGross + result.calculation.totalGrossPay,
        totalNet: acc.totalNet + result.calculation.netPay,
        totalHours: acc.totalHours + result.timeEntries?.reduce((h, entry) => h + (entry.hours || 0), 0) || 0,
      }),
      { totalGross: 0, totalNet: 0, totalHours: 0 }
    );
  };

  const handleQuickSelect = (weeks: number) => {
    const start = startOfWeek(new Date());
    const end = endOfWeek(addWeeks(start, weeks - 1));
    setStartDate(start);
    setEndDate(end);
    setStartDateOpen(false);
    setEndDateOpen(false);
  };

  const summary = getTotalSummary();

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-7xl max-h-[95vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5" />
              Payroll Preview & Impact Analysis
            </DialogTitle>
            <DialogDescription>
              Real-time compensation calculations and detailed payroll impact analysis
            </DialogDescription>
          </DialogHeader>

          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col overflow-hidden">
            <TabsList className="grid w-full grid-cols-4 flex-shrink-0">
              <TabsTrigger value="overview" className="flex items-center gap-2">
                <Calculator className="h-4 w-4" />
                Overview
              </TabsTrigger>
              <TabsTrigger value="calculations" className="flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Real-time Engine
              </TabsTrigger>
              <TabsTrigger value="payslips" className="flex items-center gap-2">
                <FileText className="h-4 w-4" />
                Payslip Preview
              </TabsTrigger>
              <TabsTrigger value="export" className="flex items-center gap-2">
                <Download className="h-4 w-4" />
                Export
              </TabsTrigger>
            </TabsList>

            {/* Overview Tab */}
            <TabsContent value="overview" className="flex-1 flex flex-col space-y-4 overflow-hidden">
              {/* Date Selection */}
              <div className="flex items-center gap-4 p-4 border rounded-lg bg-muted/20 flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium">Period:</span>
                  <Popover open={startDateOpen} onOpenChange={setStartDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        key={startDate?.getTime() || 'no-start-date'}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "justify-start text-left font-normal",
                          !startDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {startDate ? format(startDate, "PPP") : <span>Pick start date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={startDate}
                        onSelect={(date) => {
                          if (date) {
                            // Create new Date object to ensure React detects the change
                            setStartDate(new Date(date));
                            setStartDateOpen(false);
                            // Auto-adjust end date if it's before the new start date
                            if (endDate && date > endDate) {
                              setEndDate(new Date(date));
                            }
                          }
                        }}
                        disabled={(date) => endDate ? date > endDate : false}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>

                  <span className="text-muted-foreground">to</span>

                  <Popover open={endDateOpen} onOpenChange={setEndDateOpen}>
                    <PopoverTrigger asChild>
                      <Button
                        key={endDate?.getTime() || 'no-end-date'}
                        variant="outline"
                        size="sm"
                        className={cn(
                          "justify-start text-left font-normal",
                          !endDate && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {endDate ? format(endDate, "PPP") : <span>Pick end date</span>}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={endDate}
                        onSelect={(date) => {
                          if (date) {
                            // Create new Date object to ensure React detects the change
                            setEndDate(new Date(date));
                            setEndDateOpen(false);
                            // Auto-adjust start date if it's after the new end date
                            if (startDate && date < startDate) {
                              setStartDate(new Date(date));
                            }
                          }
                        }}
                        disabled={(date) => startDate ? date < startDate : false}
                        initialFocus
                        className="p-3 pointer-events-auto"
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <Separator orientation="vertical" className="h-6" />
                
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleQuickSelect(1)}>
                    This Week
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleQuickSelect(2)}>
                    2 Weeks
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleQuickSelect(4)}>
                    1 Month
                  </Button>
                </div>

                <div className="ml-auto flex gap-2">
                  <Button size="sm" onClick={() => refetch()} disabled={isLoading}>
                    <Calculator className="mr-2 h-4 w-4" />
                    Recalculate
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    disabled={!payrollPreview?.length}
                    onClick={() => setShowExportDialog(true)}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export
                  </Button>
                </div>
              </div>

              {/* Summary Cards */}
              <div className="grid grid-cols-4 gap-2 flex-shrink-0">
                <Card>
                  <CardHeader className="pb-1">
                    <CardDescription>Total Employees</CardDescription>
                    <CardTitle className="text-xl">
                      {payrollPreview?.length || 0}
                    </CardTitle>
                  </CardHeader>
                </Card>
                
                <Card>
                  <CardHeader className="pb-1">
                    <CardDescription>Total Hours</CardDescription>
                    <CardTitle className="text-xl">
                      {summary.totalHours.toFixed(1)}
                    </CardTitle>
                  </CardHeader>
                </Card>
                
                <Card>
                  <CardHeader className="pb-1">
                    <CardDescription>Gross Payroll</CardDescription>
                    <CardTitle className="text-xl text-green-600">
                      {formatCurrency(summary.totalGross)}
                    </CardTitle>
                  </CardHeader>
                </Card>
                
                <Card>
                  <CardHeader className="pb-1">
                    <CardDescription>Net Payroll</CardDescription>
                    <CardTitle className="text-xl">
                      {formatCurrency(summary.totalNet)}
                    </CardTitle>
                  </CardHeader>
                </Card>
              </div>

              {/* Loading/Error States */}
              {isLoading && (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
                  <span className="ml-2 text-muted-foreground">Calculating payroll preview...</span>
                </div>
              )}

              {error && (
                <Card className="border-destructive">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-destructive">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Calculation Error</span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {error.message || 'Failed to calculate payroll preview'}
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Pending Time Entries Warning */}
              {payrollPreview && payrollPreview.some(result => 
                result.timeEntries?.some(entry => entry.status === 'pending')
              ) && (
                <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-amber-700 dark:text-amber-500">
                      <AlertCircle className="h-5 w-5" />
                      <span className="font-medium">Pending Time Entries Detected</span>
                    </div>
                    <p className="text-sm text-amber-600 dark:text-amber-400 mt-1">
                      Some time entries in this period are pending approval. They are included in calculations but may need approval before finalizing payroll.
                    </p>
                  </CardContent>
                </Card>
              )}

              {/* Employee Results */}
              {payrollPreview && payrollPreview.length > 0 && (
                <div className="flex-1 flex flex-col min-h-0">
                  <div className="flex items-center justify-between mb-4 flex-shrink-0">
                    <h3 className="text-lg font-semibold">Employee Breakdown</h3>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setActiveTab('payslips')}
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      View Payslips
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto border rounded-lg p-4 space-y-4">
                  
                    {payrollPreview.map((result) => (
                      <Card key={result.profileId} className="cursor-pointer hover:shadow-md transition-shadow">
                        <CardHeader className="pb-3">
                          <div className="flex items-center justify-between">
                            <div>
                              <CardTitle className="text-base">{result.employeeName}</CardTitle>
                              <CardDescription>
                                {result.timeEntries?.length || 0} time entries • {' '}
                                {(result.timeEntries?.reduce((acc, entry) => acc + (entry.hours || 0), 0) || 0).toFixed(1)} hours
                              </CardDescription>
                            </div>
                            
                            <div className="text-right">
                              <p className="text-lg font-semibold">
                                {formatCurrency(result.calculation.netPay)}
                              </p>
                              <p className="text-sm text-muted-foreground">
                                Gross: {formatCurrency(result.calculation.totalGrossPay)}
                              </p>
                            </div>
                          </div>
                        </CardHeader>

                        <CardContent className="pt-0">
                          <div className="space-y-3">
                            {/* Calculation Summary */}
                            <div className="grid grid-cols-3 gap-4 text-sm">
                              <div>
                                <p className="text-muted-foreground">Regular Hours</p>
                                <p className="font-medium">
                                  {result.calculation.calculationSummary.regularHours} @ {formatCurrency(result.calculation.calculationSummary.regularRate)}
                                </p>
                              </div>
                              
                              {result.calculation.calculationSummary.overtimeHours > 0 && (
                                <div>
                                  <p className="text-muted-foreground">Overtime Hours</p>
                                  <p className="font-medium">
                                    {result.calculation.calculationSummary.overtimeHours} @ {formatCurrency(result.calculation.calculationSummary.overtimeRate || 0)}
                                  </p>
                                </div>
                              )}
                              
                              {result.calculation.calculationSummary.holidayHours > 0 && (
                                <div>
                                  <p className="text-muted-foreground">Holiday Hours</p>
                                  <p className="font-medium">
                                    {result.calculation.calculationSummary.holidayHours} @ {formatCurrency(result.calculation.calculationSummary.holidayRate || 0)}
                                  </p>
                                </div>
                              )}
                              
                              {result.calculation.calculationSummary.nonScheduledDayHours > 0 && (
                                <div>
                                  <p className="text-muted-foreground">Non-Scheduled Day Hours</p>
                                  <p className="font-medium">
                                    {result.calculation.calculationSummary.nonScheduledDayHours}
                                  </p>
                                </div>
                              )}
                            </div>

                            {/* Line Items */}
                            {result.calculation.lineItems.length > 0 && (
                              <div className="space-y-2">
                                <Separator />
                                <p className="text-sm font-medium text-muted-foreground">Pay Components</p>
                                
                                <div className="grid gap-2">
                                  {result.calculation.lineItems.slice(0, 3).map((item, index) => (
                                    <div key={index} className="flex items-center justify-between text-sm">
                                      <span className="flex items-center gap-2">
                                        {item.category === 'earnings' && <CheckCircle className="h-3 w-3 text-green-500" />}
                                        {item.category === 'deductions' && <AlertCircle className="h-3 w-3 text-red-500" />}
                                        {item.description}
                                      </span>
                                      <span className={`font-medium ${
                                        item.category === 'deductions' ? 'text-red-600' : 'text-green-600'
                                      }`}>
                                        {item.category === 'deductions' ? '-' : ''}{formatCurrency(item.gross_amount)}
                                      </span>
                                    </div>
                                  ))}
                                  {result.calculation.lineItems.length > 3 && (
                                    <p className="text-xs text-muted-foreground text-center">
                                      +{result.calculation.lineItems.length - 3} more items
                                    </p>
                                  )}
                                </div>
                              </div>
                            )}
                            
                            <div className="flex justify-end">
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => {
                                  setSelectedEmployee(result);
                                  setActiveTab('payslips');
                                }}
                              >
                                View Details
                              </Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* No Results */}
              {payrollPreview && payrollPreview.length === 0 && !isLoading && (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <Calculator className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No Payroll Data</p>
                    <p className="text-sm">
                      No time entries or compensation rules found for the selected period and employees.
                    </p>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Real-time Calculations Tab */}
            <TabsContent value="calculations">
              <RealTimeCalculationEngine
                selectedEmployeeIds={selectedEmployeeIds.length > 0 ? selectedEmployeeIds : employees?.map(e => e.id) || []}
                startDate={startDate}
                endDate={endDate}
                onCalculationComplete={(results) => {
                  devLog('Calculation completed:', results);
                }}
                autoRefresh={false}
              />
            </TabsContent>

            {/* Payslip Preview Tab */}
            <TabsContent value="payslips" className="flex-1 flex flex-col space-y-4 overflow-hidden">
              {payrollPreview && payrollPreview.length > 0 ? (
                <>
                  <div className="flex items-center justify-between flex-shrink-0">
                    <h3 className="text-lg font-semibold">Individual Payslip Previews</h3>
                    <div className="flex gap-2">
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => setShowExportDialog(true)}
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export All
                      </Button>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-y-auto space-y-8 pr-2">
                    {(selectedEmployee ? [selectedEmployee] : payrollPreview).map((result) => (
                      <PayslipPreviewCard
                        key={result.profileId}
                        employeeData={result}
                        payPeriodStart={startDate}
                        payPeriodEnd={endDate}
                        showSensitiveData={true}
                        onExport={async (fmt) => {
                          try {
                            if (fmt === 'pdf') {
                              const blob = await generatePreviewPayslipPdf(result, startDate, endDate, { currencyCode });
                              const filename = `payslip_preview_${result.employeeName.replace(/\s+/g, '_')}_${format(startDate, 'yyyy-MM-dd')}.pdf`;
                              downloadBlob(blob, filename);
                              toast.success(`PDF exported for ${result.employeeName}`);
                            }
                          } catch (err) {
                            console.error('Export failed:', err);
                            toast.error('Failed to export payslip');
                          }
                        }}
                      />
                    ))}
                  </div>
                  
                  {selectedEmployee && (
                    <div className="flex justify-center flex-shrink-0">
                      <Button 
                        variant="outline"
                        onClick={() => setSelectedEmployee(null)}
                      >
                        View All Payslips
                      </Button>
                    </div>
                  )}
                </>
              ) : (
                <Card className="border-dashed">
                  <CardContent className="p-8 text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium">No Payslips Available</p>
                    <p className="text-sm">
                      Generate payroll calculations first to view individual payslips.
                    </p>
                    <Button 
                      className="mt-4"
                      onClick={() => setActiveTab('overview')}
                    >
                      Go to Overview
                    </Button>
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Export Tab */}
            <TabsContent value="export">
              <Card>
                <CardHeader>
                  <CardTitle>Export Payroll Data</CardTitle>
                  <CardDescription>
                    Export comprehensive payroll reports and individual payslips
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Button 
                      variant="outline" 
                      className="h-20 flex-col gap-2"
                      onClick={() => setShowExportDialog(true)}
                      disabled={!payrollPreview?.length}
                    >
                      <Download className="h-6 w-6" />
                      <span>Export All Data</span>
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="h-20 flex-col gap-2"
                      disabled={!payrollPreview?.length}
                      onClick={() => {
                        setSelectedEmployee(null);
                        setActiveTab('payslips');
                      }}
                    >
                      <FileText className="h-6 w-6" />
                      <span>Individual Payslips</span>
                    </Button>
                  </div>
                  
                  <div className="text-sm text-muted-foreground">
                    <p>Available formats: PDF, Excel, CSV</p>
                    <p>Export includes: Summary, detailed calculations, and compliance data</p>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      {showExportDialog && payrollPreview && (
        <PayrollExportDialog
          open={showExportDialog}
          onOpenChange={setShowExportDialog}
          payrollData={payrollPreview}
          payPeriodStart={startDate}
          payPeriodEnd={endDate}
        />
      )}
    </>
  );
}